import { z } from 'zod/v4';

export const PV_WATTS_CONTRACT_VERSION = 1;
export const PV_WATTS_MODEL = 'pvwattsv8';
export const PV_WATTS_LABEL = 'PVWatts weather estimate';
export const PV_WATTS_DOCUMENTATION =
  'https://developer.nlr.gov/docs/solar/pvwatts/v8/';

const point = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
const plane = z.object({
  tilt: z.number().min(0).max(90),
  azimuth: z.number().min(0).lt(360),
});
const moduleSchema = z.object({
  panelId: z.string().min(1).max(100),
  moduleId: z.string().min(1).max(100),
  productSpecId: z.uuid().optional(),
  widthMeters: z.number().positive(),
  heightMeters: z.number().positive(),
  capacityWatts: z.number().positive(),
  orientation: z.enum(['PORTRAIT', 'LANDSCAPE']),
  corners: z.array(point).length(4),
});

/** Captured server inputs, not a GraphQL client certification payload. */
export const pvWattsCaptureSchema = z
  .object({
    version: z.literal(PV_WATTS_CONTRACT_VERSION),
    designId: z.uuid(),
    revision: z.number().int().positive(),
    analysis: z
      .object({
        publicId: z.uuid(),
        version: z.string().regex(/^[a-f0-9]{64}$/),
      })
      .optional(),
    location: z
      .object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        addressId: z.uuid(),
        coordinateSource: z.enum(['stored_site_address', 'confirmed_site_pin']),
        coordinatesSetAt: z.string().datetime(),
      })
      .refine((p) => p.latitude !== 0 || p.longitude !== 0),
    arrays: z
      .array(
        z.object({
          roofId: z.number().int().nonnegative(),
          roofPolygon: z.object({
            outer: z.array(point).min(3).max(2000),
            holes: z.array(z.array(point).min(3).max(2000)).max(100),
          }),
          roofPlane: plane,
          /** Absolute angle above horizontal and compass azimuth, already applied. */
          appliedPlane: plane,
          mounting: z.enum(['roof_fixed', 'tilt_frame']),
          /** Optional for historical reads; missing proof cannot certify new finance. */
          committedFaceFingerprint: z.string().max(1_000_000).optional(),
          modules: z.array(moduleSchema).min(1).max(5000),
        })
      )
      .min(1)
      .max(50),
    assumptions: z.object({
      moduleType: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      lossesPercent: z.number().min(-5).max(99),
      dcAcRatio: z.number().positive().max(10),
      inverterEfficiencyPercent: z.number().min(90).max(99.5),
      groundCoverageRatio: z.number().min(0.01).max(0.99),
      dataset: z.literal('nsrdb'),
      radiusMiles: z.number().int().min(0).max(1000),
      provenance: z.enum([
        'documented_payaca_defaults',
        /** Payaca's defaults, with the DC/AC ratio from the selected inverters' rated AC. */
        'payaca_defaults_selected_inverter_dc_ac',
        'explicit_server_configuration',
      ]),
    }),
  })
  .superRefine((capture, ctx) => {
    const roofs = new Set<number>();
    const panels = new Set<string>();
    for (const array of capture.arrays) {
      if (roofs.has(array.roofId))
        ctx.addIssue({ code: 'custom', message: 'Duplicate roof' });
      roofs.add(array.roofId);
      const capacity =
        array.modules.reduce((sum, m) => sum + m.capacityWatts, 0) / 1000;
      if (capacity < 0.05 || capacity > 500000)
        ctx.addIssue({ code: 'custom', message: 'Unsupported array capacity' });
      for (const module of array.modules) {
        if (panels.has(module.panelId))
          ctx.addIssue({ code: 'custom', message: 'Duplicate panel' });
        panels.add(module.panelId);
      }
    }
  });
export type PvWattsCapture = z.infer<typeof pvWattsCaptureSchema>;

/**
 * Model defaults are explicit; 14% is the example's generic system loss
 * assumption, not site shading. `dcAcRatio` 1.2 is PVWatts' own default and
 * applies only when no inverter rating is known; otherwise use
 * {@link pvWattsAssumptions} with the design's real ratio.
 */
export const PV_WATTS_DEFAULT_ASSUMPTIONS: PvWattsCapture['assumptions'] = {
  moduleType: 0,
  lossesPercent: 14,
  dcAcRatio: 1.2,
  inverterEfficiencyPercent: 96,
  groundCoverageRatio: 0.4,
  dataset: 'nsrdb',
  radiusMiles: 100,
  provenance: 'documented_payaca_defaults',
};

/**
 * PVWatts takes any positive `dc_ac_ratio`; the capture schema caps it at 10.
 * Bounds keep a bad catalogue rating from producing a nonsensical model.
 */
export const PV_WATTS_DC_AC_RATIO_RANGE = { min: 0.01, max: 10 } as const;

/**
 * The design's DC/AC ratio: array kWp over the summed rated AC output of the
 * chosen inverter units, clamped to {@link PV_WATTS_DC_AC_RATIO_RANGE} and
 * rounded to 3 dp. Null when either side is unknown, so the caller keeps
 * PVWatts' 1.2 default rather than inventing one.
 */
export const pvWattsDcAcRatio = (
  arrayKwp: number,
  inverterAcKw: number | null | undefined
): number | null => {
  if (
    !Number.isFinite(arrayKwp) ||
    arrayKwp <= 0 ||
    inverterAcKw == null ||
    !Number.isFinite(inverterAcKw) ||
    inverterAcKw <= 0
  )
    return null;
  const ratio = Math.min(
    PV_WATTS_DC_AC_RATIO_RANGE.max,
    Math.max(PV_WATTS_DC_AC_RATIO_RANGE.min, arrayKwp / inverterAcKw)
  );
  return Math.round(ratio * 1000) / 1000;
};

/** Default assumptions with the design's DC/AC ratio when one is known. */
export const pvWattsAssumptions = (
  dcAcRatio: number | null | undefined
): PvWattsCapture['assumptions'] =>
  dcAcRatio == null
    ? PV_WATTS_DEFAULT_ASSUMPTIONS
    : {
        ...PV_WATTS_DEFAULT_ASSUMPTIONS,
        dcAcRatio,
        provenance: 'payaca_defaults_selected_inverter_dc_ac',
      };

export const pvWattsParameters = (
  capture: PvWattsCapture,
  array: PvWattsCapture['arrays'][number]
) => ({
  lat: capture.location.latitude,
  lon: capture.location.longitude,
  system_capacity:
    array.modules.reduce((sum, m) => sum + m.capacityWatts, 0) / 1000,
  module_type: capture.assumptions.moduleType,
  // Fixed roof: 1. Ventilated fixed tilt frames: 0. Never select a tracker.
  array_type: array.mounting === 'roof_fixed' ? 1 : 0,
  tilt: array.appliedPlane.tilt,
  azimuth: array.appliedPlane.azimuth,
  losses: capture.assumptions.lossesPercent,
  dc_ac_ratio: capture.assumptions.dcAcRatio,
  inv_eff: capture.assumptions.inverterEfficiencyPercent,
  gcr: capture.assumptions.groundCoverageRatio,
  dataset: capture.assumptions.dataset,
  radius: capture.assumptions.radiusMiles,
  timeframe: 'monthly' as const,
});

export const pvWattsCanonicalInputs = (capture: PvWattsCapture): string =>
  JSON.stringify({
    model: PV_WATTS_MODEL,
    capture: pvWattsCaptureSchema.safeParse(capture).data ?? null,
  });

export const matchesPvWattsParameters = (
  inputs: Record<string, unknown>,
  parameters: ReturnType<typeof pvWattsParameters>
): boolean =>
  Object.entries(parameters).every(([key, value]) => {
    const received = inputs[key];
    if (typeof value === 'string') return received === value;
    return (
      (typeof received === 'number' ||
        (typeof received === 'string' && received.trim() !== '')) &&
      Number(received) === value
    );
  });

const monthly = z.array(z.number().nonnegative()).length(12);
export const pvWattsResponseSchema = z
  .object({
    inputs: z.record(z.string(), z.unknown()),
    errors: z.array(z.string()).max(100),
    warnings: z.array(z.string()).max(100),
    // Official V8 example: 8.0.0; live NLR V8 response checked 7 Oct 2026: 8.5.0.
    // Both retain the pvwattsv8 / monthly AC kWh contract validated below.
    // Keep an explicit allowlist: other versions require compatibility review.
    version: z.enum(['8.0.0', '8.5.0']),
    ssc_info: z.object({
      version: z.number().int(),
      build: z.string(),
      module: z.literal(PV_WATTS_MODEL),
    }),
    station_info: z.object({
      lat: z.number().min(-90).max(90),
      lon: z.number().min(-180).max(180),
      elev: z.number(),
      tz: z.number(),
      location: z.string(),
      city: z.string(),
      state: z.string(),
      solar_resource_file: z.string(),
      weather_data_source: z.string().min(1),
      distance: z.number().nonnegative().optional(),
    }),
    outputs: z.object({
      ac_annual: z.number().nonnegative(),
      ac_monthly: monthly,
      dc_monthly: monthly.optional(),
      poa_monthly: monthly.optional(),
      solrad_monthly: monthly.optional(),
      solrad_annual: z.number().nonnegative().optional(),
      capacity_factor: z.number().nonnegative().optional(),
    }),
  })
  .refine(
    (response) =>
      response.errors.length === 0 &&
      Math.abs(
        response.outputs.ac_monthly.reduce((sum, v) => sum + v, 0) -
          response.outputs.ac_annual
      ) <= Math.max(0.01, response.outputs.ac_annual * 1e-6)
  );

export const pvWattsSnapshotSchema = z
  .object({
    version: z.literal(PV_WATTS_CONTRACT_VERSION),
    source: z.literal('pvwatts_v8'),
    model: z.literal(PV_WATTS_MODEL),
    label: z.literal(PV_WATTS_LABEL),
    snapshotId: z.uuid(),
    calculatedAt: z.string().datetime(),
    inputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    capturedInputs: pvWattsCaptureSchema,
    units: z.literal('kWh_ac'),
    annualAcKwh: z.number().nonnegative(),
    monthlyAcKwh: monthly,
    limitations: z.literal(
      'No detailed site obstacle or row shading model. Generic system losses are assumptions; AC output already includes modelled losses and inverter conversion.'
    ),
    arrays: z
      .array(
        z.object({
          roofId: z.number().int().nonnegative(),
          parameters: z.record(z.string(), z.union([z.string(), z.number()])),
          response: pvWattsResponseSchema,
        })
      )
      .min(1)
      .max(50),
  })
  .superRefine((snapshot, ctx) => {
    if (!matchesPvWattsSnapshotData(snapshot, snapshot.capturedInputs))
      ctx.addIssue({
        code: 'custom',
        message: 'Weather snapshot totals or source inputs do not match',
      });
  });
export type PvWattsSnapshot = z.infer<typeof pvWattsSnapshotSchema>;

/** Cache verification is server-side only; this is not proof for client-supplied snapshots. */
export const matchesPvWattsSnapshotData = (
  snapshot: PvWattsSnapshot,
  capture: PvWattsCapture
): boolean => {
  if (
    pvWattsCanonicalInputs(snapshot.capturedInputs) !==
      pvWattsCanonicalInputs(capture) ||
    snapshot.arrays.length !== capture.arrays.length
  )
    return false;
  let annual = 0;
  const months = Array<number>(12).fill(0);
  for (const [index, array] of capture.arrays.entries()) {
    const result = snapshot.arrays[index];
    const params = pvWattsParameters(capture, array);
    if (
      !result ||
      result.roofId !== array.roofId ||
      Object.keys(result.parameters).length !== Object.keys(params).length ||
      !Object.entries(params).every(
        ([key, value]) =>
          Object.hasOwn(result.parameters, key) &&
          result.parameters[key] === value
      ) ||
      !matchesPvWattsParameters(result.response.inputs, params)
    )
      return false;
    annual += result.response.outputs.ac_annual;
    result.response.outputs.ac_monthly.forEach((value, month) => {
      months[month] += value;
    });
  }
  return (
    annual === snapshot.annualAcKwh &&
    months.every((value, month) => value === snapshot.monthlyAcKwh[month])
  );
};
